import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  TextField,
  Button,
  Alert,
  Chip,
  Grid,
  IconButton,
  Tooltip,
  CircularProgress,
  Divider,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
} from '@mui/material';
import {
  Google as GoogleIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  Security as SecurityIcon,
  ContentCopy as ContentCopyIcon,
  CheckCircle as CheckCircleIcon,
  Info as InfoIcon,
  Add as AddIcon,
} from '@mui/icons-material';
import { motion } from 'motion/react';
import { toast } from 'react-toastify';
import { getGoogleIdPRedirectURI, generatePreviewAlias } from '../../utils/constants';

// Google brand color
const accentColor = '#4285F4';
const accentColorLight = '#E8F0FE';
const accentColorHover = '#3367D6';

const GoogleIdPList = ({
  googleIdPs = [],
  googleIdPsLimit = 10,
  createGoogleIdP,
  updateGoogleIdP,
  deleteGoogleIdP,
  listGoogleIdPs,
  validateGoogleIdPConfig,
  loading,
  isInitialLoading = false
}) => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingIdP, setEditingIdP] = useState(null); // null for create, IdP object for edit
  const [showClientSecret, setShowClientSecret] = useState(false);
  const [validating, setValidating] = useState(false);
  const [validation, setValidation] = useState(null);
  const [copiedRedirectURI, setCopiedRedirectURI] = useState(false);
  const [formData, setFormData] = useState({
    clientId: '',
    clientSecret: '',
    displayName: '',
    enabled: true,
    trustEmail: true,
    storeToken: false,
    addReadTokenRoleOnCreate: false,
    syncMode: 'FORCE'
  });

  // Calculate preview alias from display name (or use actual alias when editing)
  const previewAlias = editingIdP
    ? editingIdP.alias
    : generatePreviewAlias(formData.displayName, 'google');

  // Get the redirect URI with the correct alias
  const redirectURI = getGoogleIdPRedirectURI(previewAlias);

  const resetForm = () => {
    setFormData({
      clientId: '',
      clientSecret: '',
      displayName: '',
      enabled: true,
      trustEmail: true,
      storeToken: false,
      addReadTokenRoleOnCreate: false,
      syncMode: 'FORCE'
    });
    setValidation(null);
    setShowClientSecret(false);
  };

  const handleOpenCreate = () => {
    resetForm();
    setEditingIdP(null);
    setDialogOpen(true);
  };

  const handleOpenEdit = (idp) => {
    setFormData({
      clientId: idp.config?.clientId || '',
      clientSecret: '', // Don't populate for security
      displayName: idp.displayName || '',
      enabled: idp.enabled !== false,
      trustEmail: idp.trustEmail !== false,
      storeToken: idp.storeToken || false,
      addReadTokenRoleOnCreate: idp.addReadTokenRoleOnCreate || false,
      syncMode: 'FORCE'
    });
    setEditingIdP(idp);
    setDialogOpen(true);
  };

  const handleClose = () => {
    setDialogOpen(false);
    setEditingIdP(null);
    resetForm();
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleCopyRedirectURI = async () => {
    try {
      await navigator.clipboard.writeText(redirectURI);
      setCopiedRedirectURI(true);
      toast.success('Redirect URI copied to clipboard');
      setTimeout(() => setCopiedRedirectURI(false), 2000);
    } catch (err) {
      toast.error('Failed to copy redirect URI');
    }
  };

  const handleValidate = async () => {
    if (!formData.clientId || !formData.clientSecret) {
      toast.error('Client ID and Client Secret are required for validation');
      return;
    }

    setValidating(true);
    try {
      const result = await validateGoogleIdPConfig({
        clientId: formData.clientId,
        clientSecret: formData.clientSecret
      });

      setValidation(result);
      if (result?.valid) {
        toast.success('Google IdP configuration is valid');
      } else {
        toast.error(`Validation failed: ${result?.error || 'Unknown error'}`);
      }
    } catch (error) {
      toast.error('Failed to validate configuration');
      setValidation({ valid: false, error: error.message });
    } finally {
      setValidating(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.displayName.trim()) {
      toast.error('Display name is required');
      return;
    }

    try {
      let success;
      if (editingIdP) {
        success = await updateGoogleIdP(editingIdP.alias, formData);
      } else {
        success = await createGoogleIdP(formData);
      }

      if (success) {
        handleClose();
      }
    } catch (error) {
      toast.error(`Failed to ${editingIdP ? 'update' : 'create'} Google Identity Provider`);
    }
  };

  const handleDelete = async (idp) => {
    const success = await deleteGoogleIdP(idp.alias);
    if (success) {
      // List will be refreshed by the hook
    }
  };

  // Minimalist input style
  const inputStyle = {
    '& .MuiOutlinedInput-root': {
      fontFamily: 'Metropolis, sans-serif',
      backgroundColor: '#FFFFFF',
      '& fieldset': {
        borderColor: '#E5E5E5',
      },
      '&:hover fieldset': {
        borderColor: accentColor,
      },
      '&.Mui-focused fieldset': {
        borderColor: accentColor,
      },
    },
    '& .MuiInputLabel-root': {
      fontFamily: 'Metropolis, sans-serif',
      color: '#666666',
      '&.Mui-focused': {
        color: accentColor,
      },
    },
    '& .MuiFormHelperText-root': {
      fontFamily: 'Metropolis, sans-serif',
    },
  };

  const canAddMore = googleIdPs.length < googleIdPsLimit;

  return (
    <Card
      elevation={0}
      sx={{
        backgroundColor: '#FFFFFF',
        border: '1px solid #E5E5E5',
        borderRadius: '8px',
      }}
    >
      <CardContent sx={{ p: 3 }}>
        {/* Header */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <GoogleIcon sx={{ fontSize: 32, color: accentColor }} />
            <Box>
              <Typography
                variant="h6"
                sx={{
                  fontFamily: 'Bitter, Georgia, serif',
                  fontWeight: 600,
                  color: '#253E66',
                }}
              >
                Google Identity Providers
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: '#999999',
                  fontFamily: 'Metropolis, sans-serif',
                }}
              >
                Configure Google OAuth 2.0 authentication for SSO ({googleIdPs.length}/{googleIdPsLimit})
              </Typography>
            </Box>
          </Box>

          <Button
            variant="contained"
            onClick={handleOpenCreate}
            disabled={loading || !canAddMore}
            startIcon={<AddIcon />}
            sx={{
              backgroundColor: accentColor,
              borderRadius: '6px',
              fontFamily: 'Metropolis, sans-serif',
              fontWeight: 600,
              textTransform: 'none',
              px: 2,
              py: 1,
              '&:hover': {
                backgroundColor: accentColorHover,
              },
              '&.Mui-disabled': {
                backgroundColor: '#E5E5E5',
                color: '#999999',
              },
            }}
          >
            Add Google IdP
          </Button>
        </Box>

        <Divider sx={{ mb: 2, borderColor: '#E5E5E5' }} />

        {/* Limit Warning */}
        {!canAddMore && (
          <Alert
            severity="warning"
            sx={{
              mb: 2,
              backgroundColor: '#FEF9E7',
              border: '1px solid #F39C1220',
              borderRadius: '6px',
              '& .MuiAlert-icon': { color: '#F39C12' },
              '& .MuiAlert-message': {
                fontFamily: 'Metropolis, sans-serif',
                color: '#253E66',
              },
            }}
          >
            Maximum number of Google Identity Providers ({googleIdPsLimit}) reached.
          </Alert>
        )}

        {/* IdP List */}
        {isInitialLoading ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', py: 6 }}>
            <CircularProgress size={40} sx={{ color: accentColor, mb: 2 }} />
            <Typography
              sx={{
                fontFamily: 'Metropolis, sans-serif',
                color: '#666666',
                fontWeight: 500,
              }}
            >
              Loading Google Identity Providers...
            </Typography>
          </Box>
        ) : googleIdPs.length > 0 ? (
          <List sx={{ p: 0 }}>
            {googleIdPs.map((idp, index) => (
              <Paper
                key={idp.alias || index}
                elevation={0}
                sx={{
                  '@keyframes fadeInItem': {
                    from: { opacity: 0, transform: 'translateY(8px)' },
                    to: { opacity: 1, transform: 'translateY(0)' },
                  },
                  animation: `fadeInItem 0.3s ease-out ${index * 0.05}s both`,
                  mb: 2,
                  border: '1px solid #E5E5E5',
                  borderRadius: '8px',
                  overflow: 'hidden',
                }}
              >
                <ListItem
                  sx={{
                    py: 2,
                    px: 3,
                    backgroundColor: '#F9FBFD',
                  }}
                >
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography
                          sx={{
                            fontFamily: 'Metropolis, sans-serif',
                            fontWeight: 600,
                            color: '#253E66',
                          }}
                        >
                          {idp.displayName || 'Google SSO'}
                        </Typography>
                        <Chip
                          label={idp.enabled !== false ? 'Enabled' : 'Disabled'}
                          size="small"
                          sx={{
                            backgroundColor: idp.enabled !== false ? '#E8F8F0' : '#FDEDEC',
                            color: idp.enabled !== false ? '#27AE60' : '#E74C3C',
                            fontFamily: 'Metropolis, sans-serif',
                            fontWeight: 500,
                            fontSize: '0.7rem',
                            height: 20,
                          }}
                        />
                      </Box>
                    }
                    secondary={
                      <Box sx={{ mt: 1 }}>
                        <Typography
                          variant="body2"
                          sx={{
                            fontFamily: 'Metropolis, sans-serif',
                            color: '#999999',
                            fontSize: '0.75rem',
                          }}
                        >
                          Alias: <span style={{ fontFamily: 'monospace', color: '#666666' }}>{idp.alias}</span>
                        </Typography>
                        {idp.config?.clientId && (
                          <Typography
                            variant="body2"
                            sx={{
                              fontFamily: 'Metropolis, sans-serif',
                              color: '#999999',
                              fontSize: '0.75rem',
                              mt: 0.5,
                            }}
                          >
                            Client ID: <span style={{ fontFamily: 'monospace', color: '#666666' }}>{idp.config.clientId.substring(0, 30)}...</span>
                          </Typography>
                        )}
                      </Box>
                    }
                  />
                  <ListItemSecondaryAction>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Tooltip title="Edit Configuration">
                        <IconButton
                          onClick={() => handleOpenEdit(idp)}
                          disabled={loading}
                          size="small"
                          sx={{
                            color: accentColor,
                            backgroundColor: accentColorLight,
                            border: '1px solid #E5E5E5',
                            borderRadius: '6px',
                            '&:hover': {
                              backgroundColor: '#D2E3FC',
                              borderColor: accentColor,
                            },
                          }}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete Configuration">
                        <IconButton
                          onClick={() => handleDelete(idp)}
                          disabled={loading}
                          size="small"
                          sx={{
                            color: '#E74C3C',
                            backgroundColor: '#FDEDEC',
                            border: '1px solid #E5E5E5',
                            borderRadius: '6px',
                            '&:hover': {
                              backgroundColor: '#FADBD8',
                              borderColor: '#E74C3C',
                            },
                          }}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </ListItemSecondaryAction>
                </ListItem>
              </Paper>
            ))}
          </List>
        ) : (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <GoogleIcon sx={{ fontSize: 64, color: '#E5E5E5', mb: 2 }} />
            <Typography
              variant="h6"
              sx={{
                color: '#999999',
                fontFamily: 'Bitter, Georgia, serif',
                mb: 1,
              }}
            >
              No Google Identity Providers Configured
            </Typography>
            <Typography
              variant="body2"
              sx={{
                color: '#999999',
                fontFamily: 'Metropolis, sans-serif',
                mb: 3,
              }}
            >
              Configure Google OAuth 2.0 to enable SSO authentication for your users.
            </Typography>
          </Box>
        )}

        {/* Create/Edit Dialog */}
        <Dialog
          open={dialogOpen}
          onClose={handleClose}
          maxWidth="md"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: '12px',
              border: '1px solid #E5E5E5',
            }
          }}
        >
          <DialogTitle sx={{
            fontFamily: 'Bitter, Georgia, serif',
            fontWeight: 600,
            color: '#253E66',
            borderBottom: '1px solid #E5E5E5',
            pb: 2,
          }}>
            {editingIdP ? 'Edit Google Identity Provider' : 'Create Google Identity Provider'}
          </DialogTitle>

          <DialogContent sx={{ pt: 3 }}>
            <motion.div
              initial={{ opacity: 0, y: 6 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: 0.1 }}
            >
            {/* Redirect URI Info */}
            <Paper
              elevation={0}
              sx={{
                backgroundColor: accentColorLight,
                border: `1px solid ${accentColor}20`,
                borderRadius: '8px',
                p: 3,
                mb: 3,
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                <InfoIcon sx={{ color: accentColor, fontSize: 24 }} />
                <Typography
                  variant="h6"
                  sx={{
                    color: accentColor,
                    fontFamily: 'Bitter, Georgia, serif',
                    fontWeight: 600,
                    fontSize: '1rem',
                  }}
                >
                  Google OAuth Configuration Required
                </Typography>
              </Box>

              <Typography
                variant="body2"
                sx={{
                  color: '#253E66',
                  fontFamily: 'Metropolis, sans-serif',
                  mb: 2,
                }}
              >
                Configure your Google OAuth application with the following redirect URI:
              </Typography>

              {/* Alias Preview */}
              <Box
                sx={{
                  backgroundColor: '#FFFFFF',
                  border: '1px solid #E5E5E5',
                  borderRadius: '6px',
                  p: 2,
                  mb: 2,
                }}
              >
                <Typography
                  variant="caption"
                  sx={{
                    color: '#999999',
                    fontFamily: 'Metropolis, sans-serif',
                    fontWeight: 500,
                  }}
                >
                  {editingIdP ? 'Alias' : 'Alias Preview (generated from Display Name)'}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    color: '#253E66',
                    fontFamily: 'monospace',
                    fontSize: '0.875rem',
                    mt: 0.5,
                  }}
                >
                  {previewAlias || 'google'}
                </Typography>
                {!editingIdP && formData.displayName && (
                  <Typography
                    variant="caption"
                    sx={{
                      color: '#F39C12',
                      fontFamily: 'Metropolis, sans-serif',
                      fontStyle: 'italic',
                      display: 'block',
                      mt: 0.5,
                    }}
                  >
                    Note: A numeric suffix may be added if this alias already exists.
                  </Typography>
                )}
              </Box>

              {/* Redirect URI */}
              <Box
                sx={{
                  backgroundColor: '#FFFFFF',
                  border: '1px solid #E5E5E5',
                  borderRadius: '6px',
                  p: 2,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 2,
                }}
              >
                <Box sx={{ flex: 1 }}>
                  <Typography
                    variant="caption"
                    sx={{
                      color: '#999999',
                      fontFamily: 'Metropolis, sans-serif',
                      fontWeight: 500,
                    }}
                  >
                    Redirect URI
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      color: '#253E66',
                      fontFamily: 'monospace',
                      fontSize: '0.875rem',
                      wordBreak: 'break-all',
                      mt: 0.5,
                    }}
                  >
                    {redirectURI}
                  </Typography>
                </Box>

                <Tooltip title={copiedRedirectURI ? 'Copied!' : 'Copy Redirect URI'}>
                  <IconButton
                    onClick={handleCopyRedirectURI}
                    size="small"
                    sx={{
                      color: copiedRedirectURI ? '#27AE60' : accentColor,
                      backgroundColor: copiedRedirectURI ? '#E8F8F0' : accentColorLight,
                      border: '1px solid #E5E5E5',
                      borderRadius: '6px',
                      '&:hover': {
                        backgroundColor: copiedRedirectURI ? '#D4EFDF' : '#D2E3FC',
                      },
                    }}
                  >
                    {copiedRedirectURI ? (
                      <CheckCircleIcon fontSize="small" />
                    ) : (
                      <ContentCopyIcon fontSize="small" />
                    )}
                  </IconButton>
                </Tooltip>
              </Box>
            </Paper>

            <Box component="form" onSubmit={handleSubmit} sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              {/* Display Name - Required for alias generation */}
              <TextField
                label="Display Name"
                name="displayName"
                value={formData.displayName}
                onChange={handleInputChange}
                placeholder="e.g., Corporate Google, Partner Google SSO"
                required
                fullWidth
                size="small"
                helperText="A unique name for this Google IdP. Used to generate the alias."
                sx={inputStyle}
              />

              {/* Client ID */}
              <TextField
                label="Google Client ID"
                name="clientId"
                value={formData.clientId}
                onChange={handleInputChange}
                placeholder="123456789012-abcdefg.apps.googleusercontent.com"
                required
                fullWidth
                size="small"
                sx={inputStyle}
              />

              {/* Client Secret */}
              <TextField
                label="Google Client Secret"
                name="clientSecret"
                type={showClientSecret ? 'text' : 'password'}
                value={formData.clientSecret}
                onChange={handleInputChange}
                placeholder={editingIdP ? 'Enter new secret to update' : 'Enter client secret'}
                required={!editingIdP}
                fullWidth
                size="small"
                InputProps={{
                  endAdornment: (
                    <IconButton
                      onClick={() => setShowClientSecret(!showClientSecret)}
                      edge="end"
                      size="small"
                      sx={{ color: '#666666' }}
                    >
                      {showClientSecret ? <VisibilityOffIcon /> : <VisibilityIcon />}
                    </IconButton>
                  ),
                }}
                sx={inputStyle}
              />

              {/* Validation */}
              {validation && (
                <Alert
                  severity={validation.valid ? 'success' : 'error'}
                  sx={{
                    backgroundColor: validation.valid ? '#E8F8F0' : '#FDEDEC',
                    border: `1px solid ${validation.valid ? '#27AE60' : '#E74C3C'}20`,
                    borderRadius: '6px',
                    '& .MuiAlert-icon': {
                      color: validation.valid ? '#27AE60' : '#E74C3C'
                    },
                    '& .MuiAlert-message': {
                      fontFamily: 'Metropolis, sans-serif',
                      color: '#253E66',
                    },
                  }}
                >
                  {validation.valid
                    ? 'Configuration is valid and ready to save'
                    : `Validation failed: ${validation.error}`
                  }
                </Alert>
              )}
            </Box>
            </motion.div>
          </DialogContent>

          <DialogActions sx={{ px: 3, py: 2, borderTop: '1px solid #E5E5E5' }}>
            <Button
              variant="outlined"
              onClick={handleValidate}
              disabled={loading || validating || !formData.clientId || !formData.clientSecret}
              sx={{
                borderColor: '#E5E5E5',
                color: accentColor,
                fontFamily: 'Metropolis, sans-serif',
                fontWeight: 600,
                textTransform: 'none',
                borderRadius: '6px',
                '&:hover': {
                  borderColor: accentColor,
                  backgroundColor: accentColorLight,
                },
              }}
              startIcon={validating ? <CircularProgress size={16} sx={{ color: accentColor }} /> : <SecurityIcon />}
            >
              {validating ? 'Validating...' : 'Validate'}
            </Button>

            <Box sx={{ flex: 1 }} />

            <Button
              variant="outlined"
              onClick={handleClose}
              disabled={loading}
              sx={{
                borderColor: '#E5E5E5',
                color: '#666666',
                fontFamily: 'Metropolis, sans-serif',
                fontWeight: 600,
                textTransform: 'none',
                borderRadius: '6px',
                '&:hover': {
                  borderColor: '#999999',
                  backgroundColor: '#F9FBFD',
                },
              }}
            >
              Cancel
            </Button>

            <Button
              variant="contained"
              onClick={handleSubmit}
              disabled={loading || !formData.clientId || !formData.displayName || (!editingIdP && !formData.clientSecret)}
              sx={{
                backgroundColor: accentColor,
                fontFamily: 'Metropolis, sans-serif',
                fontWeight: 600,
                textTransform: 'none',
                borderRadius: '6px',
                px: 3,
                '&:hover': {
                  backgroundColor: accentColorHover,
                },
                '&.Mui-disabled': {
                  backgroundColor: '#E5E5E5',
                  color: '#999999',
                },
              }}
              startIcon={loading ? <CircularProgress size={16} sx={{ color: 'white' }} /> : <GoogleIcon />}
            >
              {loading ? 'Saving...' : (editingIdP ? 'Update' : 'Create')}
            </Button>
          </DialogActions>
        </Dialog>
      </CardContent>
    </Card>
  );
};

export default GoogleIdPList;
